import moment from 'moment-timezone';

export const MAX_DROPDOWN_ITEMS = 12;

export interface IDropdownItem {
  label: string;
  startDate: Date;
  endDate: Date;
}

export function generateWeeks(timezone: string): IDropdownItem[] {
  const items: IDropdownItem[] = [];
  const startOfWeek = moment().tz(timezone).startOf('isoWeek');
  for (let i = 0; i < MAX_DROPDOWN_ITEMS; i++) {
    const mon = startOfWeek.clone().subtract(i, 'weeks');
    const sun = mon.clone().add(6, 'days');
    items.push({
      label: `${mon.format('D MMM')} - ${sun.format('D MMM')}`,
      startDate: mon.toDate(),
      endDate: sun.endOf('day').toDate(),
    });
  }
  return items;
}

export function generateMonths(timezone: string): IDropdownItem[] {
  const items: IDropdownItem[] = [];
  const startOfMonth = moment().tz(timezone).startOf('month');
  for (let i = 0; i < MAX_DROPDOWN_ITEMS; i++) {
    const m = startOfMonth.clone().subtract(i, 'months');
    items.push({
      label: m.format('MMM YYYY'),
      startDate: m.clone().startOf('month').toDate(),
      endDate: m.clone().endOf('month').toDate(),
    });
  }
  return items;
}

/**
 * ISO weeks intersecting the given month, ordered oldest-first. Ranges are
 * clipped to the month boundaries and labelled with the in-month days
 * (e.g. "1 - 7", "29 - 31"). Weeks that have not started yet as of `now`
 * (gym timezone) are omitted: their range would start in the future and
 * fail validation, so they must not be offered as options.
 */
export function generateWeeksInMonth(
  timezone: string,
  monthStart: Date,
  now: Date = new Date(),
): IDropdownItem[] {
  const items: IDropdownItem[] = [];
  const month = moment(monthStart).tz(timezone);
  const monthStartDay = month.clone().startOf('month');
  const monthEndDay = month.clone().endOf('month');
  const weekStart = monthStartDay.clone().startOf('isoWeek');
  const nowInTz = moment(now).tz(timezone);
  while (weekStart.isSameOrBefore(monthEndDay, 'day')) {
    const weekEnd = weekStart.clone().endOf('isoWeek');
    const clippedStart = weekStart.isBefore(monthStartDay) ? monthStartDay : weekStart;
    // Weeks are chronological: stop at the first one that has not started.
    if (clippedStart.isAfter(nowInTz, 'day')) {
      break;
    }
    const clippedEnd = weekEnd.isAfter(monthEndDay) ? monthEndDay : weekEnd;
    items.push({
      label: `${clippedStart.format('D')} - ${clippedEnd.format('D')}`,
      startDate: clippedStart.toDate(),
      endDate: clippedEnd.toDate(),
    });
    weekStart.add(1, 'weeks');
  }
  return items;
}

export function generateYears(timezone: string): IDropdownItem[] {
  const items: IDropdownItem[] = [];
  const startOfYear = moment().tz(timezone).startOf('year');
  for (let i = 0; i < MAX_DROPDOWN_ITEMS; i++) {
    const y = startOfYear.clone().subtract(i, 'years');
    items.push({
      label: y.format('YYYY'),
      startDate: y.clone().startOf('year').toDate(),
      endDate: y.clone().endOf('year').toDate(),
    });
  }
  return items;
}
